import { X } from 'lucide-react';
import React, { useState } from 'react';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import '../styles/Contactus.css';
import '../styles/Faqs.css';
import Faqs from '../components/Faqs'; 
import { baseUrl } from '../config';
import { useNavigate } from 'react-router-dom';

const CategoryCard = ({ icon, title, description, onClick }) => (
  <div className="category-card" onClick={onClick}>
    <div className="icon">{icon}</div>
    <h3>{title}</h3>
    <p>{description}</p>
    <div className="arrow">›</div>
  </div>
);

const FormField = ({ type, name, placeholder, value, onChange, required }) => {
  if (type === 'textarea') {
    return (
      <textarea
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="contact-form-input contact-form-textarea"
      />
    );
  }
  return (
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      className="contact-form-input"
    />
  );
};

const ContactForm = ({ onClose, title, fields }) => {
  const [formData, setFormData] = useState(
    fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {})
  );
  const [formStatus, setFormStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // ✅ Check if this category requires login
  const requiresLogin = title !== 'General Support';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus('');

    const dataToSend = {
      ...formData,
      category: title,
    };

    try {
      // ✅ Get token if user is logged in
      const token = localStorage.getItem("access_token");
      
      const headers = {
        'Content-Type': 'application/json',
      };

      // ✅ Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${baseUrl}/contacts`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        setFormStatus('✅ Form submitted successfully! We\'ll get back to you soon.');
        setFormData(fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}));
        
        // Close form after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else if (response.status === 401) {
        // ✅ Handle login requirement
        setFormStatus(`⚠️ Login required for ${title} submissions. Redirecting to login...`);
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (response.status === 422) {
        const errorResponse = await response.json();
        setFormStatus('❌ Please check all fields and try again.');
      } else {
        const errorResponse = await response.json();
        setFormStatus('❌ An error occurred. Please try again.');
      }
    } catch (error) {
      setFormStatus('❌ Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`contact-form-overlay ${title ? 'active' : ''}`}>
      <div className="contact-form-container">
        <button 
          className="contact-form-close-btn" 
          onClick={onClose} 
          disabled={isSubmitting}
        >
          <X size={24} />
        </button>
        <h2 className="contact-form-title">{title}</h2>
        
        {/* ✅ Show login notice for categories that require it */}
        {requiresLogin && (
          <div className="contact-form-login-notice">
            <p>ℹ️ Login required for this category</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="contact-form">
          {fields.map((field) => (
            <FormField
              key={field.name}
              type={field.type}
              name={field.name}
              placeholder={field.placeholder}
              value={formData[field.name]}
              onChange={handleChange}
              required={field.required}
            />
          ))}
          
          {/* ✅ Enhanced Submit Button with Loading State */}
          <button 
            type="submit" 
            className={`contact-form-submit-btn ${isSubmitting ? 'loading' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="contact-form-spinner"></span>
                <span>Submitting...</span>
              </>
            ) : (
              'Submit'
            )}
          </button>
        </form>
        
        {/* ✅ Status Message */}
        {formStatus && (
          <p className={`contact-form-status ${
            formStatus.includes('successfully') ? 'success' : 
            formStatus.includes('Login required') ? 'warning' : 
            'error'
          }`}>
            {formStatus}
          </p>
        )}
      </div>
    </div>
  );
};

const ContactUs = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = [
    {
      icon: '🔧',
      title: 'General Support',
      description: 'Browse our resources, search the knowledge base, or submit a ticket to our support team.',
      fields: [
        { type: 'text', name: 'name', placeholder: 'Name', required: true },
        { type: 'email', name: 'email', placeholder: 'Email', required: true },
        { type: 'text', name: 'subject', placeholder: 'Subject', required: true },
        { type: 'textarea', name: 'message', placeholder: 'Message', required: true },
      ],
    },
    {
      icon: '💡',
      title: 'Suggest an integration',
      description: 'Suggest a new source or destination to be added to Segment.',
      fields: [
        { type: 'text', name: 'name', placeholder: 'Name', required: true },
        { type: 'email', name: 'email', placeholder: 'Email', required: true },
        { type: 'text', name: 'integration', placeholder: 'Integration Name', required: true },
        { type: 'textarea', name: 'message', placeholder: 'Integration Description', required: true },
      ],
    },
    {
      icon: '💼',
      title: 'Contact Sales',
      description: 'Ask about our Business tier products, pricing, or request a demo.',
      fields: [
        { type: 'text', name: 'name', placeholder: 'Name', required: true },
        { type: 'email', name: 'email', placeholder: 'Email', required: true },
        { type: 'tel', name: 'phoneNumber', placeholder: 'Phone', required: false },
        { type: 'text', name: 'company', placeholder: 'Company', required: true },
        { type: 'textarea', name: 'message', placeholder: 'Message', required: true },
      ],
    },
    {
      icon: '💻',
      title: 'Technical support',
      description: 'Can\'t find your answer elsewhere? Submit a ticket with our Technical Support team.',
      fields: [
        { type: 'text', name: 'name', placeholder: 'Name', required: true },
        { type: 'email', name: 'email', placeholder: 'Email', required: true },
        { type: 'text', name: 'subject', placeholder: 'Subject', required: true },
        { type: 'textarea', name: 'message', placeholder: 'Issue Description', required: true },
      ],
    },
    {
      icon: '🤝',
      title: 'Partner Program',
      description: 'Access sales support, technical training, and co-marketing opportunities.',
      fields: [
        { type: 'text', name: 'name', placeholder: 'Name', required: true },
        { type: 'email', name: 'email', placeholder: 'Email', required: true },
        { type: 'text', name: 'company', placeholder: 'Company', required: true },
        { type: 'text', name: 'partner_type', placeholder: 'Partner Type', required: true },
        { type: 'textarea', name: 'message', placeholder: 'Message', required: true },
      ],
    },
    {
      icon: '💳',
      title: 'Billing and account',
      description: 'Ask about billing, pricing, or account and workspace concerns.',
      fields: [
        { type: 'text', name: 'name', placeholder: 'Name', required: true },
        { type: 'email', name: 'email', placeholder: 'Email', required: true },
        { type: 'text', name: 'accountId', placeholder: 'Account ID', required: true },
        { type: 'text', name: 'subject', placeholder: 'Subject', required: true },
        { type: 'textarea', name: 'message', placeholder: 'Message', required: true },
      ],
    },
  ];

  const handleCategoryClick = (title) => {
    setSelectedCategory(title);
  };

  const handleCloseForm = () => {
    setSelectedCategory(null);
  };

  return (
    <div>
      <SEO
        title="Contact Us"
        description="Get in touch with the ProducerBuddy team for support, partnership inquiries, or feedback. We're here to help filmmakers and production teams succeed."
        url="https://producerbuddy.pilomation.com/contactus"
      />
      <Nav />
      <div className="contact-us-page">
        <h1>How can we help you?</h1>
        <p>Choose a category below to email the support team so we can get back to you as quickly as possible.</p>
        <div className="category-grid">
          {categories.map((category) => (
            <CategoryCard
              key={category.title}
              icon={category.icon}
              title={category.title}
              description={category.description}
              onClick={() => handleCategoryClick(category.title)}
            />
          ))}
        </div>
        {selectedCategory && (
          <ContactForm
            onClose={handleCloseForm}
            title={selectedCategory}
            fields={categories.find(cat => cat.title === selectedCategory).fields}
          />
        )}
      </div>
      <Faqs/>
    </div>
  );
};

export default ContactUs;
